import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  ApiError,
  authApi,
  isPending,
  profileApi,
  type ProfilePatch,
  type RegisterInput,
  type User,
} from './api.js';

interface AuthState {
  user: User | null;
  loading: boolean;
  allowRegistration: boolean;
  login: (email: string, password: string) => Promise<void>;
  /** Rend `true` quand la demande attend une validation : pas de session. */
  register: (input: RegisterInput) => Promise<boolean>;
  logout: () => Promise<void>;
  updateProfile: (patch: ProfilePatch) => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [allowRegistration, setAllowRegistration] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const [config, session] = await Promise.allSettled([authApi.config(), authApi.me()]);
      if (cancelled) return;

      if (config.status === 'fulfilled') setAllowRegistration(config.value.allowRegistration);
      // Un 401 au demarrage est le cas nominal "pas encore connecte".
      if (session.status === 'fulfilled') setUser(session.value.user);
      else if (!(session.reason instanceof ApiError) || session.reason.status !== 401) {
        console.error('session illisible', session.reason);
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setUser((await authApi.login(email, password)).user);
  }, []);

  const register = useCallback(async (input: RegisterInput) => {
    const result = await authApi.register(input);
    if (isPending(result)) return true;
    setUser(result.user);
    return false;
  }, []);

  const updateProfile = useCallback(async (patch: ProfilePatch) => {
    setUser((await profileApi.update(patch)).user);
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout().catch(() => undefined);
    setUser(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, loading, allowRegistration, login, register, logout, updateProfile }),
    [user, loading, allowRegistration, login, register, logout, updateProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth doit etre utilise dans <AuthProvider>');
  return context;
}
